export interface AppInfo {
  id: string;
  name: string;
  description: string;
  path: string;
  version: string;
  features: string[];
  technologies: string[];
}

export interface PersonalWebsite {
  name: string;
  url: string | null;
  domain: string | null;
  type: 'achievement' | 'update' | 'placeholder';
}

export interface Artifact {
  name: string;
  description: string;
  url: string | null;
}

export interface TreeNode {
  name: string;
  children?: TreeNode[];
  path?: string;
  type?: string;
}

// Badge types for ecosystem nodes
export type BadgeType = 'HOT' | 'NEW' | 'DEV' | 'PTR' | 'OLD' | 'LIVE';

// Priority levels for ecosystem nodes
export type PriorityLevel = 'High' | 'Medium' | 'Low';

export interface DomainNode {
  name: string;
  description?: string;
  priority?: PriorityLevel;
  url?: string;
  type?: string;
  icon?: string;
  badge?: BadgeType;
  heroImage?: string;
  heroAlt?: string;
  heroUrl?: string; // Live iframe preview URL
  scrollOffset?: number; // CSS transform translateY to scroll iframe content
  tech?: string[]; // Technology stack
  children?: DomainNode[];
}

export interface DomainData {
  name: string;
  description: string;
  url?: string;
  type?: string;
  children: DomainNode[];
}

export interface HomeTemplateProps {
  title?: string;
  subtitle?: string;
  description?: string;
  apps?: AppInfo[];
  showProjectStructure?: boolean;
  showRadialGraph?: boolean;
  showPersonalWebsites?: boolean;
  showArtifacts?: boolean;
  showTreeStructure?: boolean;
  customSections?: React.ReactNode[];
}

// Badge color mapping
export const BadgeColors: Record<BadgeType, string> = {
  HOT: '#ff4500',
  NEW: '#28a745',
  DEV: '#ffc107',
  PTR: '#17a2b8',
  OLD: '#6c757d',
  LIVE: '#10b981'
};

// Priority color mapping
export const PriorityColors: Record<PriorityLevel, string> = {
  High: '#10b981',
  Medium: '#3b82f6',
  Low: '#6b7280'
};

// ═══════════════════════════════════════════════════════════════
// SHOWCASE TIERED ARCHITECTURE TYPES
// ═══════════════════════════════════════════════════════════════

/**
 * 3D TIER - Platform Project Cards (Hero Viewport Carousel)
 * Large cards with live iframe previews, rotating in 3D space
 */
export interface PlatformCard {
  id: string;
  name: string;
  description: string;
  url: string;
  icon: string;
  badge?: BadgeType;
  heroUrl?: string;       // Live iframe preview URL
  heroImage?: string;     // Fallback static image
  heroAlt?: string;
  scrollOffset?: number;  // Iframe scroll position
  tech?: string[];        // Technology stack tags
  color?: string;         // Card accent color
  tier?: 'spire' | 'cylinder' | 'cube';  // Palace of Culture tier assignment
}

/**
 * 2D TIER - Document Module Slices (Horizontal Sweep)
 * Smaller cards for PDFs, presentations, certifications
 */
export type DocumentType = 'pdf' | 'presentation' | 'certification' | 'image' | 'video';

export interface DocumentSlice {
  id: string;
  title: string;
  description: string;
  url: string;
  icon: string;
  type: DocumentType;
  thumbnail?: string;     // Preview thumbnail
  date?: string;          // Publication/creation date
  fileSize?: string;      // e.g., "2.4 MB"
}

/**
 * 1D TIER - Inline Text Items (Ticker/Supporting Material)
 * Non-visual text-heavy content for inline display
 */
export type TextItemCategory = 'skill' | 'tool' | 'certification' | 'achievement' | 'link';

export interface TextItem {
  id: string;
  label: string;
  category: TextItemCategory;
  url?: string;           // Optional link
  icon?: string;
  description?: string;
}

/**
 * Showcase Section Configuration
 */
export interface ShowcaseTier {
  id: string;
  label: string;
  icon: string;
  color: string;
  dimension: '3D' | '2D' | '1D';
}

export const SHOWCASE_TIERS: ShowcaseTier[] = [
  { id: 'platforms', label: 'PLATFORMS', icon: '✨', color: '#a855f7', dimension: '3D' },
  { id: 'documents', label: 'DOCUMENTS', icon: '📄', color: '#fbbf24', dimension: '2D' },
  { id: 'resources', label: 'RESOURCES', icon: '🔗', color: '#22d3ee', dimension: '1D' }
];
